/**
 * ResumePanel — top-of-page banner that lets a teacher pick up an
 * in-flight Math Engine workflow exactly where they left off.
 *
 * Two stacked sections:
 *   1. A prominent "enter the results" banner for any workflow parked
 *      at the diagnostic-results-entry or exit-ticket-review step. This
 *      is the most easily-missed continuation point, so it gets a
 *      stronger visual treatment.
 *   2. A quieter grid for workflows that are mid-unit but not waiting
 *      on input.
 *
 * Delete actions are gated behind an AlertDialog because deleting a
 * unit removes ALL its child artifacts from the Library.
 */
import React, { useState } from "react";
import { Clock, ClipboardList, Loader2, PlayCircle, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { STAGE_PROGRESS } from "./stageCopy";
import { formatRelTime } from "./utils";

const ResumePanel = ({ workflows, onResume, onDelete }) => {
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  if (!workflows.length) return null;

  // Workflows parked on the two teacher-input steps move to the top
  // banner — they're the most easily-forgotten continuation point.
  const needsInputKeys = new Set(["results_entry", "exit_review"]);
  const needsInput = workflows.filter((w) => needsInputKeys.has(w.next_step_key));
  const others = workflows.filter((w) => !needsInputKeys.has(w.next_step_key));

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await onDelete?.(confirmTarget);
      setConfirmTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const titleFor = (wf) =>
    wf.mini_unit_label
      ? `${wf.grade} · ${wf.mini_unit_label} ${wf.mini_unit_name}`
      : `${wf.grade} · Unit ${wf.unit_number} ${wf.unit_name}`;

  return (
    <>
      <div
        className="space-y-3 mb-8"
        data-testid="math-engine-resume-panel"
      >
        {needsInput.length > 0 && (
          <div
            data-testid="math-engine-needs-input-banner"
            className="rounded-2xl border-2 border-[#96ded1] bg-[#96ded1]/15 p-5 md:p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="h-5 w-5 text-[#1c2d3d]" />
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#1c2d3d]">
                Pick up where you left off · enter results
              </p>
            </div>
            <p className="text-sm text-[#4a6984] font-medium mb-4 leading-relaxed">
              You generated a{" "}
              {needsInput[0].next_step_key === "results_entry"
                ? "diagnostic"
                : "lesson"}{" "}
              but haven't logged the class's results yet. Click below to enter
              them — the engine will draft the next adaptive lesson based on
              what students did.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {needsInput.map((wf) => {
                const key = `${wf.grade}|${wf.unit_number}|${wf.mini_unit_label || ""}`;
                return (
                  <div
                    key={key}
                    className="relative rounded-xl bg-white border-2 border-[#1c2d3d] hover:shadow-md p-4 transition-all"
                  >
                    <button
                      type="button"
                      onClick={() => onResume(wf)}
                      data-testid={`math-engine-needs-input-${wf.grade.replace(/\s+/g, "-")}-${wf.unit_number}-${wf.mini_unit_label || "u"}`}
                      className="block w-full text-left pr-8"
                    >
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#1c2d3d]/70 mb-1">
                        {formatRelTime(wf.latest_artifact.created_at)} ·
                        awaiting results
                      </p>
                      <p className="font-extrabold text-[#1c2d3d] tracking-tight mb-2">
                        {titleFor(wf)}
                      </p>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1c2d3d] text-white text-xs font-extrabold">
                        <PlayCircle className="h-3.5 w-3.5" />{" "}
                        {wf.next_step_label}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmTarget(wf);
                      }}
                      aria-label={`Delete ${titleFor(wf)}`}
                      data-testid={`math-engine-delete-${wf.grade.replace(/\s+/g, "-")}-${wf.unit_number}-${wf.mini_unit_label || "u"}`}
                      className="absolute top-2 right-2 h-7 w-7 rounded-md grid place-items-center text-[#4a6984]/60 hover:text-rose-600 hover:bg-rose-50 transition"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {others.length > 0 && (
          <div className="ae-card p-5 md:p-6 bg-[#f4f5f7]/70 border-[#96ded1]/40">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-[#1c2d3d]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/80">
                Resume where you left off
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {others.slice(0, 6).map((wf) => {
                const stage = STAGE_PROGRESS[wf.latest_artifact.output_type] || {
                  idx: 0,
                  label: "In progress",
                };
                const key = `${wf.grade}|${wf.unit_number}|${wf.mini_unit_label || ""}`;
                return (
                  <div
                    key={key}
                    className="relative rounded-xl border-2 border-[#96ded1]/60 bg-white hover:border-[#96ded1] hover:shadow-sm p-4 transition-all"
                  >
                    <button
                      type="button"
                      onClick={() => onResume(wf)}
                      className="block w-full text-left pr-7"
                      data-testid={`math-engine-resume-${wf.grade.replace(/\s+/g, "-")}-${wf.unit_number}-${wf.mini_unit_label || "u"}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                          Step {stage.idx} / 5 · {stage.label}
                        </p>
                        <span className="text-[10px] font-bold text-[#4a6984]/60">
                          {formatRelTime(wf.latest_artifact.created_at)}
                        </span>
                      </div>
                      <p className="font-bold text-[#1c2d3d] tracking-tight">
                        {titleFor(wf)}
                      </p>
                      <p className="mt-1 text-xs text-[#4a6984]/85 flex items-center gap-1.5">
                        <PlayCircle className="h-3.5 w-3.5" /> {wf.next_step_label}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmTarget(wf);
                      }}
                      aria-label={`Delete ${titleFor(wf)}`}
                      data-testid={`math-engine-delete-${wf.grade.replace(/\s+/g, "-")}-${wf.unit_number}-${wf.mini_unit_label || "u"}`}
                      className="absolute top-2 right-2 h-7 w-7 rounded-md grid place-items-center text-[#4a6984]/60 hover:text-rose-600 hover:bg-rose-50 transition"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AlertDialog
        open={!!confirmTarget}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1c2d3d]">
              Delete this Math Engine unit?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a6984]">
              <span className="font-bold text-[#1c2d3d]">
                {confirmTarget ? titleFor(confirmTarget) : ""}
              </span>{" "}
              and every lesson generated under it (activation, diagnostic,
              lessons, exit reviews, unit summary) will be removed from your
              Library. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={deleting}
              data-testid="math-engine-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white font-extrabold"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting…
                </>
              ) : (
                "Delete unit"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ResumePanel;
